import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useInfraGraphOverview, useInfraGraphStack } from '../hooks/useInfraGraph.js';
import { ErrorState } from '../components/feedback/ErrorState/index.js';
import { InfraGraphView } from '../components/features/infra-graph/InfraGraphView.js';
import {
  buildDetailElements,
  buildOverviewElements,
} from '../components/features/infra-graph/infra-graph-elements.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business インフラ構成図ページ。Pulumi state 由来の infra-graph を stack 俯瞰で表示し、stack クリックで内部リソース（GCP 1 リソース = 1 ノード）へドリルダウンする。公式 GCP アイコン付き。認証済み社員全員が閲覧可。
 * @graph-connects useInfraGraphOverview [calls] stack 俯瞰取得フック
 * @graph-connects useInfraGraphStack [calls] stack 詳細取得フック
 * @graph-connects InfraGraphView [calls] cytoscape 構成図描画コンポーネント
 */
export const Route = createFileRoute('/infra-graph')({
  component: InfraGraphPage,
});

/** @graph-connects none */
function InfraGraphPage() {
  const { t } = useTranslation();
  const { data: overview, isLoading, error, refetch } = useInfraGraphOverview();
  const [selectedStack, setSelectedStack] = useState<string | null>(null);
  const { data: detail, isLoading: isDetailLoading } = useInfraGraphStack(selectedStack);

  const elements = useMemo(() => {
    if (selectedStack) {
      return detail ? buildDetailElements(detail) : [];
    }
    return overview ? buildOverviewElements(overview) : [];
  }, [selectedStack, detail, overview]);

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('infraGraph.title')}
        </h1>
        <div className="glass-card p-6 rounded-xl">
          <div className="h-96 skeleton-glass rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        message={t('common.error')}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const meta = overview?.meta;
  const stackLabel = selectedStack?.replace(/^cortex-prod-/, '');
  const detailMeta = detail ? { nodes: detail.nodes.length, edges: detail.edges.length } : null;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] animate-fade-in">
      {/* ヘッダー（パンくず + メタ + 戻る） */}
      <div className="flex items-center gap-3 mb-3">
        <button
          type="button"
          onClick={() => {
            setSelectedStack(null);
          }}
          disabled={!selectedStack}
          className="px-3 py-1.5 text-sm rounded-md border border-[var(--color-border)] text-[var(--color-text-secondary)] enabled:hover:bg-[var(--color-bg-hover)] disabled:opacity-40"
        >
          ← {t('infraGraph.overview')}
        </button>
        <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
          {t('infraGraph.title')}
          {stackLabel && (
            <span className="text-[var(--color-accent-primary)]"> › {stackLabel}</span>
          )}
        </h1>
        <span className="text-xs text-[var(--color-text-muted)]">
          {selectedStack
            ? detailMeta &&
              t('infraGraph.detailMeta', { nodes: detailMeta.nodes, edges: detailMeta.edges })
            : meta &&
              t('infraGraph.overviewMeta', {
                stacks: meta.stackCount,
                resources: meta.resourceCount,
                edges: meta.crossEdgeCount,
              })}
        </span>
      </div>

      {selectedStack && isDetailLoading ? (
        <div className="flex-1 glass-card rounded-lg flex items-center justify-center">
          <div className="loading-spinner" />
        </div>
      ) : (
        <InfraGraphView
          elements={elements}
          layoutMode={selectedStack ? 'detail' : 'overview'}
          onSelectStack={setSelectedStack}
        />
      )}
    </div>
  );
}
